import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { SortableTable } from '../ui/SortableTable';
import { FreshnessCell } from './FreshnessCell';
import type { ColumnDef, SortState } from '../ui/SortableTable';
import type { DossierProvinceRow } from '../../types/dossier';
import type { CrystalBallData } from '../../types/intel';
import { classifyEffect } from '../../utils/effectClassification';
import { formatEffectDuration } from '../../utils/effectDuration';

interface EffectsTabProps {
  provinces: DossierProvinceRow[];
  currentTick: number;
  sortState: SortState;
  onSort: (key: string) => void;
}

// Attacker-perspective chip colors
const CATEGORY_COLORS = {
  DEFENSE: COLORS.negative,   // red — target is hardened, avoid
  DEBUFF:  COLORS.positive,   // green — target is weakened, opportunity
  BUFF:    COLORS.warning,    // yellow — target is stronger, bigger payoff
} as const;

function EffectChip({ effectType, ticksRemaining, indefinite }: { effectType: string; ticksRemaining: number; indefinite: boolean }) {
  const category = classifyEffect(effectType);
  const color = CATEGORY_COLORS[category];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        marginRight: 4,
        marginBottom: 2,
        border: `1px solid ${color}`,
        color,
        borderRadius: 2,
        fontSize: 11,
        whiteSpace: 'nowrap',
      }}
    >
      {effectType} ({formatEffectDuration({ ticksRemaining, indefinite })})
    </span>
  );
}

function renderEffectsByCategory(cb: CrystalBallData | null, category: 'DEFENSE' | 'DEBUFF' | 'BUFF') {
  if (!cb?.activeEffects?.length) return <span style={{ color: COLORS.text.muted }}>—</span>;
  const filtered = cb.activeEffects.filter((e) => classifyEffect(e.effectType) === category);
  if (!filtered.length) return <span style={{ color: COLORS.text.muted }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {filtered.map((e) => (
        <EffectChip key={e.effectType} effectType={e.effectType} ticksRemaining={e.ticksRemaining} indefinite={e.indefinite} />
      ))}
    </div>
  );
}

export function EffectsTab({ provinces, currentTick, sortState, onSort }: EffectsTabProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<DossierProvinceRow>[] = [
    {
      key: 'provinceName',
      header: 'Province',
      sticky: true,
      sortable: true,
      sortAccessor: (r) => r.provinceName,
      render: (r) => (
        <span>
          <span style={{ color: COLORS.text.primary, fontWeight: 500 }}>{r.provinceName}</span>
          <span style={{ color: COLORS.text.muted, marginLeft: 4, fontSize: 11 }}>({r.location})</span>
        </span>
      ),
    },
    {
      key: 'defenses',
      header: 'Defenses',
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null ?? null;
        return renderEffectsByCategory(cb, 'DEFENSE');
      },
    },
    {
      key: 'debuffs',
      header: 'Debuffs (Opportunities)',
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null ?? null;
        return renderEffectsByCategory(cb, 'DEBUFF');
      },
    },
    {
      key: 'buffs',
      header: 'Buffs',
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null ?? null;
        return renderEffectsByCategory(cb, 'BUFF');
      },
    },
    {
      key: 'freshness',
      header: 'Freshness',
      sortable: true,
      sortAccessor: (r) => r.latestCb?.capturedAtTick ?? -1,
      render: (r) => (
        <FreshnessCell capturedAtTick={r.latestCb?.capturedAtTick ?? null} currentTick={currentTick} />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => {
        if (r.latestCb) return null;
        const params = new URLSearchParams({
          targetId: r.provinceId,
          targetName: r.provinceName,
          targetLoc: r.location,
        });
        return (
          <button
            onClick={() => navigate(`/thievery?${params.toString()}`)}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text.tertiary,
              cursor: 'pointer',
              borderRadius: 2,
            }}
          >
            Gather CB
          </button>
        );
      },
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={provinces}
      getRowKey={(r) => r.provinceId}
      sortState={sortState}
      onSort={onSort}
    />
  );
}
